import { createSelector } from 'reselect';
import { compose, filter, isEqual, reject, get, find } from 'lodash/fp';
import { flow } from 'fp-ts/function';
import { createAction, DateTimeService } from '@mews/framework';
import { padStartZero } from '@optimus-web/core';
import { ApplicationState, PaymentCard } from '@mews-commander/types';
import { getStringProp } from '@mews/fp';
import {
    CardPaymentMethod,
    CreditCard,
    PaymentCardFormat,
    PaymentCardState,
    PaymentProviderUsageMethod,
} from '@mews-commander/data-access';
import { localizeEnum, notEmpty } from '@mews-commander/utils';
import parentSelector from '../selector';
import prefix from '../prefix';
import { getTodayStructuredDate } from '../selectors/session/dateTime';
import { createBaseEntitySelectors, createEntitiesReducer, createEntitySelectorByAccountId } from './utils';
import { PAYMENT_CARDS_FETCH_SUCCEEDED } from './actionTypes';

export const NAME = 'paymentCards';

const PAYMENT_CARD_ADDED = prefix('PAYMENT_CARD_ADDED');

export const setPaymentCards = (paymentCards: PaymentCard[]) =>
    createAction(PAYMENT_CARDS_FETCH_SUCCEEDED, { entities: paymentCards });
export const setPaymentCard = (paymentCard: PaymentCard) => createAction(PAYMENT_CARD_ADDED, paymentCard);

export const [getPaymentCards, getPaymentCardsByIds, getPaymentCardsByOwnerId, getPaymentCard] =
    createBaseEntitySelectors<ApplicationState, PaymentCard, typeof NAME>(parentSelector, NAME);

export const getPaymentCardType = compose(get('type'), getPaymentCard);

export const getPaymentCardsByAccountId = createEntitySelectorByAccountId(getPaymentCards);

export const getNonExpiredPaymentCards = createSelector(
    getPaymentCardsByAccountId,
    getTodayStructuredDate,
    (cards, { year: currentYear, month: currentMonth }) => {
        const current = `${currentYear}-${padStartZero(currentMonth + 1)}`;
        return reject(
            ({ expiration, state }) => (expiration && expiration < current) || state === PaymentCardState.Disabled,
            cards
        );
    }
);

export const getCustomerGatewayPaymentCards = createSelector(
    getNonExpiredPaymentCards,
    filter<PaymentCard>({ kind: PaymentProviderUsageMethod.Gateway })
);

export const getPaymentCardById = createSelector(getPaymentCardsByAccountId, getStringProp('id'), (cards, id) =>
    find({ id }, cards)
);

export const hasCustomerValidExternalCreditCard = flow(getCustomerGatewayPaymentCards, notEmpty);

export type PaymentCardMethod = CardPaymentMethod | CreditCard;

export const createCardLabel = ({
    format,
    lastDigits,
    type,
}: Pick<PaymentCardMethod, 'format' | 'lastDigits' | 'type'>) => {
    const cardLabel = `${localizeEnum('PaymentCardType', type)} ${lastDigits}`;

    if (isEqual(format, PaymentCardFormat.Virtual)) {
        return `${cardLabel} (${localizeEnum('PaymentCardFormat', PaymentCardFormat.Virtual)})`;
    }
    return cardLabel;
};

export const parseExpirationDate = (monthDate: string, timezone: string) => {
    const dateTime = DateTimeService.fromMonth(monthDate, timezone);
    return DateTimeService.endOfMonth(dateTime);
};

export const paymentCardsReducer = createEntitiesReducer({
    FETCH_SUCCESS: PAYMENT_CARDS_FETCH_SUCCEEDED,
    ADD_SUCCESS: PAYMENT_CARD_ADDED,
});
